import { useState } from "react";
import RichTextEditor, { RichTextDisplay } from "./RichTextEditor";
import { submitBroadcastResponse, awardBroadcastXP } from "./broadcasts";
import { writeUserNotification } from "./userNotifications";
import { formatCallTime } from "./calls";

export default function BroadcastCard({ broadcast, currentUser, onDismiss, onXpAwarded }) {
  const [comment, setComment] = useState("");
  const [selections, setSelections] = useState([]);
  const [starredOption, setStarredOption] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const xpReward = Number(broadcast.xpReward || 0);

  async function submit(overrideComment, overrideSelections, overrideStarred) {
    setSubmitting(true);
    try {
      await submitBroadcastResponse({
        broadcastId: broadcast.id,
        broadcastTitle: broadcast.title,
        userId: currentUser.uid,
        userName: currentUser.name,
        userAvatar: currentUser.avatar,
        type: broadcast.type,
        comment: overrideComment ?? comment,
        selections: overrideSelections ?? selections,
        starred: overrideStarred !== undefined ? overrideStarred : starredOption,
        programTag: currentUser.programTag || "",
        callId: broadcast.callId || null,
        options: broadcast.options || [],
      });

      // Dismiss immediately — don't let XP award failure block this
      onDismiss(broadcast.id);

      // Notify admin that someone responded to their broadcast
      if (broadcast.createdBy && broadcast.createdBy !== currentUser.uid) {
        writeUserNotification({
          toUserId: broadcast.createdBy,
          type: "comment",
          message: `${currentUser.name || "A client"} responded to your broadcast "${broadcast.title || "Broadcast"}"`,
          fromName: currentUser.name || "",
          fromAvatar: currentUser.avatar || "",
          screen: "admin_broadcasts",
        });
      }

      // Award XP after dismissing so any Firestore rules error doesn't leave the card stuck
      if (xpReward > 0) {
        awardBroadcastXP(broadcast.id, currentUser.uid, xpReward, broadcast.title)
          .then(() => { if (onXpAwarded) onXpAwarded(xpReward); })
          .catch((err) => console.error("XP award failed:", err));
      }
    } catch (err) {
      console.error("Broadcast submit failed:", err);
    } finally {
      setSubmitting(false);
    }
  }

  function toggleSelection(option) {
    if (broadcast.allowMultiple) {
      setSelections((prev) =>
        prev.includes(option) ? prev.filter((s) => s !== option) : [...prev, option]
      );
    } else {
      setSelections([option]);
    }
  }

  const typeColors = {
    announcement: { bg: "#f8fbff", border: "#bfdbfe", accent: "#2563eb", tag: "📢 Announcement" },
    question:     { bg: "#fff7fd", border: "#7dd3fc", accent: "#1d4ed8", tag: "💬 Question" },
    poll:         { bg: "#faf5ff", border: "#93c5fd", accent: "#1e3a8a", tag: "📊 Poll" },
    call_poll:    { bg: "#f0fdf4", border: "#6ee7b7", accent: "#059669", tag: "📞 Call Time Poll" },
  };
  const colors = typeColors[broadcast.type] || typeColors.announcement;

  return (
    <div style={{
      background: colors.bg,
      borderRadius: "20px",
      border: `2px solid ${colors.border}`,
      padding: "18px",
      boxShadow: "0 4px 16px rgba(121,94,148,0.1)",
    }}>
      {/* Tag */}
      <div style={{ fontSize: "11px", fontWeight: "bold", color: colors.accent, marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {colors.tag}
      </div>

      {/* Title + body */}
      <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px", marginBottom: "6px" }}>{broadcast.title}</div>
      <RichTextDisplay html={broadcast.body} style={{ color: "#243b53", fontSize: "14px", lineHeight: "1.6", marginBottom: xpReward > 0 ? "10px" : "16px" }} />

      {/* XP badge */}
      {xpReward > 0 && (
        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          background: "#e0f2fe",
          border: "2px solid #7dd3fc",
          borderRadius: "999px",
          padding: "4px 12px",
          marginBottom: "14px",
          fontSize: "13px",
          fontWeight: "bold",
          color: "#1e3a5f",
        }}>
          💎 +{xpReward} XP for responding
        </div>
      )}

      {/* ── Announcement ── */}
      {broadcast.type === "announcement" && (
        <button
          onClick={() => submit("", [])}
          disabled={submitting}
          style={{
            width: "100%",
            padding: "12px",
            borderRadius: "14px",
            border: "none",
            background: colors.accent,
            color: "#fff",
            fontWeight: "bold",
            cursor: submitting ? "default" : "pointer",
            fontSize: "14px",
          }}
        >
          {submitting ? "Marking..." : xpReward > 0 ? `✓ Got it (+${xpReward} XP)` : "✓ Got it"}
        </button>
      )}

      {/* ── Question ── */}
      {broadcast.type === "question" && (
        <div style={{ display: "grid", gap: "10px" }}>
          <RichTextEditor
            value={comment}
            onChange={setComment}
            placeholder="Type your response..."
            rows={3}
          />
          <button
            onClick={() => submit(comment, [])}
            disabled={submitting || !comment}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "14px",
              border: "none",
              background: comment ? colors.accent : "#bfdbfe",
              color: "#fff",
              fontWeight: "bold",
              cursor: comment.trim() && !submitting ? "pointer" : "default",
              fontSize: "14px",
            }}
          >
            {submitting ? "Submitting..." : xpReward > 0 ? `Submit (+${xpReward} XP)` : "Submit"}
          </button>
        </div>
      )}

      {/* ── Call time poll ── */}
      {broadcast.type === "call_poll" && (
        <div style={{ display: "grid", gap: "10px" }}>
          <div style={{ color: "#047857", fontSize: "12px", marginBottom: "2px" }}>
            Select all times that work for you — optionally ⭐ star your preferred time.
          </div>
          {(broadcast.options || []).map((option) => {
            const selected = selections.includes(option);
            const isStarred = starredOption === option;
            return (
              <div key={option} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button
                  onClick={() => {
                    const nowSelected = !selected;
                    setSelections((prev) =>
                      prev.includes(option) ? prev.filter((s) => s !== option) : [...prev, option]
                    );
                    if (!nowSelected && starredOption === option) setStarredOption(null);
                  }}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    borderRadius: "14px",
                    border: selected ? "2px solid #059669" : "2px solid #a7f3d0",
                    background: selected ? "#d1fae5" : "#f0fdf4",
                    color: "#065f46",
                    fontWeight: selected ? "bold" : "normal",
                    cursor: "pointer",
                    textAlign: "left",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <span style={{
                    width: "18px", height: "18px", borderRadius: "4px",
                    border: selected ? "2px solid #059669" : "2px solid #6ee7b7",
                    background: selected ? "#059669" : "transparent",
                    flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {selected && <span style={{ color: "#fff", fontSize: "11px", fontWeight: "bold" }}>✓</span>}
                  </span>
                  {formatCallTime(option)}
                </button>
                {selected && (
                  <button
                    onClick={() => setStarredOption(isStarred ? null : option)}
                    title={isStarred ? "Remove preferred" : "Mark as preferred"}
                    style={{
                      flexShrink: 0,
                      width: "40px", height: "40px",
                      borderRadius: "12px",
                      border: isStarred ? "2px solid #f59e0b" : "2px solid #d1fae5",
                      background: isStarred ? "#fef3c7" : "#f0fdf4",
                      cursor: "pointer",
                      fontSize: "18px",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {isStarred ? "⭐" : "☆"}
                  </button>
                )}
              </div>
            );
          })}
          {starredOption && (
            <div style={{ fontSize: "12px", color: "#b45309", background: "#fef3c7", borderRadius: "8px", padding: "6px 10px" }}>
              ⭐ Preferred: {formatCallTime(starredOption)}
            </div>
          )}
          <button
            onClick={() => submit("", selections, starredOption)}
            disabled={submitting || selections.length === 0}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "14px",
              border: "none",
              background: selections.length > 0 ? "#059669" : "#a7f3d0",
              color: "#fff",
              fontWeight: "bold",
              cursor: selections.length > 0 && !submitting ? "pointer" : "default",
              fontSize: "14px",
              marginTop: "4px",
            }}
          >
            {submitting ? "Submitting vote..." : "Submit Vote"}
          </button>
        </div>
      )}

      {/* ── Poll ── */}
      {broadcast.type === "poll" && (
        <div style={{ display: "grid", gap: "10px" }}>
          <div style={{ color: "#4b6b8a", fontSize: "12px", marginBottom: "2px" }}>
            {broadcast.allowMultiple ? "Select all that apply" : "Select one"}
          </div>
          {(broadcast.options || []).map((option) => {
            const selected = selections.includes(option);
            return (
              <button
                key={option}
                onClick={() => toggleSelection(option)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "14px",
                  border: selected ? `2px solid ${colors.accent}` : `2px solid ${colors.border}`,
                  background: selected ? "#e0f2fe" : "#f5faff",
                  color: "#1e3a5f",
                  fontWeight: selected ? "bold" : "normal",
                  cursor: "pointer",
                  textAlign: "left",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{
                  width: "18px", height: "18px", borderRadius: broadcast.allowMultiple ? "4px" : "999px",
                  border: selected ? `2px solid ${colors.accent}` : "2px solid #93c5fd",
                  background: selected ? colors.accent : "transparent",
                  flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {selected && <span style={{ color: "#fff", fontSize: "11px", fontWeight: "bold" }}>✓</span>}
                </span>
                {option}
              </button>
            );
          })}
          <button
            onClick={() => submit("", selections)}
            disabled={submitting || selections.length === 0}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "14px",
              border: "none",
              background: selections.length > 0 ? colors.accent : "#bfdbfe",
              color: "#fff",
              fontWeight: "bold",
              cursor: selections.length > 0 && !submitting ? "pointer" : "default",
              fontSize: "14px",
              marginTop: "4px",
            }}
          >
            {submitting ? "Submitting..." : xpReward > 0 ? `Submit (+${xpReward} XP)` : "Submit"}
          </button>
        </div>
      )}

    </div>
  );
}
